// ============================================================================
// USE SECOND SCREEN WINDOW - Window Management Hook
// Opens/detects/focuses second screen popup windows (supports multiple screens)
// ============================================================================

import { useState, useRef, useCallback, useEffect } from 'react';

const POLL_CLOSED_MS = 1_000;

interface ScreenWindowState {
  isOpen: boolean;
  windowRef: Window | null;
  pollRef: ReturnType<typeof setInterval> | null;
}

/**
 * Manages a single second-screen popup window.
 * screenNumber: 1 = "Second Screen", 2 = "Third Screen", etc.
 */
export function useSecondScreenWindow(sessionId: string, screenNumber: number = 1) {
  const [isOpen, setIsOpen] = useState(false);
  const windowRef = useRef<Window | null>(null);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, []);

  const open = useCallback(() => {
    // If already open, focus it
    if (windowRef.current && !windowRef.current.closed) {
      windowRef.current.focus();
      return;
    }

    const windowName = screenNumber === 1
      ? `spectrum-second-screen-${sessionId}`
      : `spectrum-screen-${screenNumber + 1}-${sessionId}`;

    const popup = window.open(
      `/second-screen/${sessionId}?screen=${screenNumber}`,
      windowName,
      [
        'width=1920',
        'height=1080',
        'menubar=no',
        'toolbar=no',
        'location=no',
        'status=no',
        'scrollbars=no',
        'resizable=yes',
      ].join(',')
    );

    if (popup) {
      windowRef.current = popup;
      setIsOpen(true);

      // Poll for closed state
      if (pollRef.current) clearInterval(pollRef.current);
      pollRef.current = setInterval(() => {
        if (popup.closed) {
          if (pollRef.current) clearInterval(pollRef.current);
          pollRef.current = null;
          setIsOpen(false);
          windowRef.current = null;
        }
      }, POLL_CLOSED_MS);
    }
  }, [sessionId, screenNumber]);

  return { isOpen, open };
}

/**
 * Manages multiple second-screen popup windows.
 * Returns controls for screen 1 (Second Screen) and screen 2 (Third Screen).
 */
export function useMultiScreenWindows(sessionId: string) {
  const screen1 = useSecondScreenWindow(sessionId, 1);
  const screen2 = useSecondScreenWindow(sessionId, 2);

  return {
    screen1,
    screen2,
    anyOpen: screen1.isOpen || screen2.isOpen,
  };
}
